/**
 * Branded fixed-point integer types.
 *
 * Every monetary and quantitative value in DNR Vyapar 2.0 is an INTEGER in a
 * defined unit. There is no floating point anywhere in the money path, which
 * is why values like the 1.6.x `cgst: 17743.7286` are unrepresentable here.
 *
 * The brands are compile-time only — they erase to `number` at runtime. Their
 * job is to stop a paise value being added to a basis-point value, which is
 * the mistake that produces silently wrong tax.
 *
 * See design §5.11.
 */
import { SCALES } from './SCALES';

declare const paiseBrand: unique symbol;
declare const milliBrand: unique symbol;
declare const bpBrand: unique symbol;

/** Integer paise. ₹1,23,456.78 is stored as 12345678. */
export type Money = number & { readonly [paiseBrand]: 'paise' };

/** Integer milli-units (0.001). 0.75 Kg is stored as 750. */
export type Quantity = number & { readonly [milliBrand]: 'milli' };

/** Integer basis points (0.01%). 18% is stored as 1800. */
export type BasisPoints = number & { readonly [bpBrand]: 'bp' };

/** Integer milli-percent (0.001%). 12.5% is stored as 12500. */
export type PercentMilli = number & { readonly [milliBrand]: 'percentMilli' };

const isInt = (v: number): boolean => Number.isFinite(v) && Number.isInteger(v);

/**
 * Construct a Money from a value already in paise.
 * @throws if not an integer — a fractional paise is a bug, not a rounding case.
 */
export function asMoney(paise: number): Money {
  if (!isInt(paise)) throw new RangeError(`Money must be whole paise, got ${paise}`);
  return paise as Money;
}

export function asQuantity(milli: number): Quantity {
  if (!isInt(milli)) throw new RangeError(`Quantity must be whole milli-units, got ${milli}`);
  return milli as Quantity;
}

export function asBasisPoints(bp: number): BasisPoints {
  if (!isInt(bp)) throw new RangeError(`BasisPoints must be an integer, got ${bp}`);
  return bp as BasisPoints;
}

export function asPercentMilli(v: number): PercentMilli {
  if (!isInt(v)) throw new RangeError(`PercentMilli must be an integer, got ${v}`);
  return v as PercentMilli;
}

/**
 * Parse user-entered rupees into paise.
 * Accepts grouping separators and up to 2 decimal places.
 *
 * `parseMoney('1,23,456.78')` → 12345678
 * `parseMoney('0.1')`         → 10
 * `parseMoney('')`            → 0
 * `parseMoney('1.005')`       → throws — silently truncating a paise is a
 *                                 data-integrity failure, not a nicety.
 */
export function parseMoney(input: string | number): Money {
  if (typeof input === 'number') {
    // SCALES.money is 1 (paise), so a rupee value must be × 100, not × 1.
    return asMoney(Math.round(input * 100));
  }
  const cleaned = String(input).replace(/[,\s₹]/g, '');
  if (cleaned === '' || cleaned === '-') return asMoney(0);
  if (!/^-?\d*\.?\d*$/.test(cleaned)) throw new RangeError(`Invalid money: "${input}"`);
  const [whole = '0', frac = ''] = cleaned.split('.');
  if (frac.length > 2) throw new RangeError(`Money has more than 2 decimals: "${input}"`);
  const paise = Number(whole || 0) * 100 + Number((frac + '00').slice(0, 2));
  return asMoney(whole.startsWith('-') ? -paise : paise);
}

/**
 * Parse a user-entered quantity into milli-units, respecting the unit's
 * declared precision. Excess precision is rejected rather than truncated:
 * 0.0005 Kg is not the same product as 0.001 Kg.
 */
export function parseQuantity(input: string | number, precision: number): Quantity {
  if (typeof input === 'number') return asQuantity(Math.round(input * SCALES.quantity));
  const cleaned = String(input).trim();
  if (cleaned === '') return asQuantity(0);
  if (!/^-?\d*\.?\d*$/.test(cleaned)) throw new RangeError(`Invalid quantity: "${input}"`);
  const decimals = cleaned.includes('.') ? (cleaned.split('.')[1]?.length ?? 0) : 0;
  if (decimals > precision) {
    throw new RangeError(
      `Quantity "${input}" exceeds ${precision} decimal places for this unit`,
    );
  }
  return asQuantity(Math.round(Number(cleaned) * SCALES.quantity));
}

/** Parse a percentage like "18", "18%" or "12.5" into basis points. */
export function parseRateBp(input: string | number): BasisPoints {
  if (typeof input === 'number') return asBasisPoints(Math.round(input * SCALES.basisPoints / 100));
  const cleaned = String(input).replace('%', '').trim();
  if (cleaned === '') return asBasisPoints(0);
  const bp = Math.round((Number(cleaned) * SCALES.basisPoints) / 100);
  if (!Number.isFinite(bp)) throw new RangeError(`Invalid rate: "${input}"`);
  return asBasisPoints(bp);
}

export const ZERO_MONEY = asMoney(0);
export const ZERO_QUANTITY = asQuantity(0);
export const ZERO_BP = asBasisPoints(0);
